import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/useAuth";
import { type Role, hasRole, extractRolesFromUser } from "../auth/claims";

interface ProtectedRouteProps {
  children: ReactNode;
  roles?: Role | Role[];
  requiredPermissions?: Array<{ resource: string; action: string }>;
  fallbackPath?: string;
  /** @deprecated Use 'roles' instead */
  requiredRoles?: string[];
}

export function ProtectedRoute({
  children,
  roles,
  requiredPermissions,
  fallbackPath = "/403",
  requiredRoles, // Keep for backward compatibility
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, hasPermission } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve the intended destination for redirect after login
    const redirectTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirectTo=${redirectTo}`} replace />;
  }

  // Extract user roles from the current user
  const userRoles = user ? extractRolesFromUser(user.roles) : [];

  // Check role requirements (support both new 'roles' prop and legacy 'requiredRoles')
  const rolesToCheck = roles || requiredRoles;
  if (rolesToCheck && rolesToCheck.length > 0) {
    const normalizedRoles = Array.isArray(rolesToCheck)
      ? rolesToCheck
      : [rolesToCheck];

    // Convert string[] to Role[] for legacy requiredRoles
    const validRoles: Role[] = ["admin", "operator", "client", "user"];
    const typedRoles = normalizedRoles.filter((role): role is Role =>
      validRoles.includes(role as Role),
    );

    const hasRequiredRole = hasRole(typedRoles, userRoles);

    if (!hasRequiredRole) {
      return <Navigate to={fallbackPath} replace />;
    }
  }

  // Check permission requirements
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasRequiredPermission = requiredPermissions.every((permission) =>
      hasPermission(permission.resource, permission.action),
    );
    if (!hasRequiredPermission) {
      return <Navigate to={fallbackPath} replace />;
    }
  }

  return <>{children}</>;
}
